// Board grid + collision and line-clear primitives.

import type { ActivePiece, Cell, PieceType, Rotation } from '../types';
import { BOARD_TOTAL_HEIGHT, BOARD_WIDTH } from '../config';
import { shapeOf } from './pieces';

export class Board {
  // Stored row-major as a flat array. Index = y * W + x.
  readonly grid: Cell[];

  constructor() {
    this.grid = new Array(BOARD_WIDTH * BOARD_TOTAL_HEIGHT).fill(null);
  }

  reset(): void {
    this.grid.fill(null);
  }

  at(x: number, y: number): Cell {
    if (x < 0 || x >= BOARD_WIDTH || y < 0 || y >= BOARD_TOTAL_HEIGHT) return null;
    return this.grid[y * BOARD_WIDTH + x]!;
  }

  set(x: number, y: number, v: Cell): void {
    if (x < 0 || x >= BOARD_WIDTH || y < 0 || y >= BOARD_TOTAL_HEIGHT) return;
    this.grid[y * BOARD_WIDTH + x] = v;
  }

  /** True if (x, y) is outside the playfield or occupied. Used for collision detection. */
  isBlocked(x: number, y: number): boolean {
    if (x < 0 || x >= BOARD_WIDTH || y >= BOARD_TOTAL_HEIGHT) return true;
    // Allow y < 0 implicitly (above buffer) — pieces can technically be there but we treat that as in-bounds.
    if (y < 0) return false;
    return this.grid[y * BOARD_WIDTH + x] !== null;
  }

  /** True if placing `piece` at its current position collides with terrain or walls. */
  collides(piece: ActivePiece): boolean {
    const shape = shapeOf(piece.type, piece.rotation);
    for (const [dx, dy] of shape) {
      if (this.isBlocked(piece.x + dx, piece.y + dy)) return true;
    }
    return false;
  }

  /** Returns the maximum downward distance the piece can travel before colliding. */
  dropDistance(piece: ActivePiece): number {
    let d = 0;
    while (!this.collides({ ...piece, y: piece.y + d + 1 })) d++;
    return d;
  }

  /** Stamps the piece's blocks into the grid using its type as the color tag. */
  lock(piece: ActivePiece): void {
    const shape = shapeOf(piece.type, piece.rotation);
    for (const [dx, dy] of shape) {
      this.set(piece.x + dx, piece.y + dy, piece.type);
    }
  }

  /** Finds full rows. */
  fullRows(): number[] {
    const full: number[] = [];
    for (let y = 0; y < BOARD_TOTAL_HEIGHT; y++) {
      let row = true;
      for (let x = 0; x < BOARD_WIDTH; x++) {
        if (this.grid[y * BOARD_WIDTH + x] === null) { row = false; break; }
      }
      if (row) full.push(y);
    }
    return full;
  }

  /** Removes the given rows and shifts everything above down. */
  clearRows(rows: number[]): void {
    if (!rows.length) return;
    const sorted = [...rows].sort((a, b) => a - b);
    for (const r of sorted) {
      for (let y = r; y > 0; y--) {
        for (let x = 0; x < BOARD_WIDTH; x++) {
          this.grid[y * BOARD_WIDTH + x] = this.grid[(y - 1) * BOARD_WIDTH + x]!;
        }
      }
      for (let x = 0; x < BOARD_WIDTH; x++) this.grid[x] = null;
    }
  }

  /** True if every cell is empty. */
  isEmpty(): boolean {
    for (let i = 0; i < this.grid.length; i++) if (this.grid[i] !== null) return false;
    return true;
  }

  /** Inject garbage rows at the bottom with a single hole column. */
  addGarbage(rowCount: number, holeColumn: number): boolean {
    if (rowCount <= 0) return true;
    // Shift everything up by rowCount. If any cells would be pushed above row 0, signal overflow.
    for (let y = 0; y < BOARD_TOTAL_HEIGHT; y++) {
      const srcY = y + rowCount;
      if (srcY >= BOARD_TOTAL_HEIGHT) {
        // fill from below with garbage
        for (let x = 0; x < BOARD_WIDTH; x++) {
          this.grid[y * BOARD_WIDTH + x] = x === holeColumn ? null : 'G';
        }
      } else {
        for (let x = 0; x < BOARD_WIDTH; x++) {
          this.grid[y * BOARD_WIDTH + x] = this.grid[srcY * BOARD_WIDTH + x]!;
        }
      }
    }
    return true;
  }

  /** Convenience for tests / snapshots — copy of the full grid. */
  snapshot(): Cell[] {
    return this.grid.slice();
  }
}

/** Type-only re-export so consumers don't have to import from pieces directly. */
export type { PieceType, Rotation };
